import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Footer = () => {
  const { t, language } = useApp();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [collaborators, setCollaborators] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchCollaborators();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/global`);
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const fetchCollaborators = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/collaborators`);
      setCollaborators(data);
    } catch (error) {
      console.error('Failed to fetch collaborators');
    }
  };

  const getTagline = () => {
    if (settings?.footer_tagline?.[language]) {
      return settings.footer_tagline[language];
    }
    // Default fallback
    if (language === 'ro') return 'Apartamente elegante de închiriat în inima Iașului.';
    if (language === 'fr') return 'Appartements élégants à louer au cœur de Iași.';
    return 'Elegant apartments for rent in the heart of Iași.';
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email });
      toast.success('Subscribed successfully!');
      setEmail('');
    } catch (error) {
      toast.error('Subscription failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="main-footer" className="bg-slate-950 border-t border-white/5 py-16">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <h3 className="text-2xl font-serif italic text-primary mb-4">RENTIASI.RO</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {getTagline()}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
              {language === 'ro' ? 'Link-uri Rapide' : language === 'en' ? 'Quick Links' : 'Liens Rapides'}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/apartments"
                  data-testid="footer-link-apartments"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t('apartments')}
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  data-testid="footer-link-about"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t('about')}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-link-contact"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t('contact')}
                </Link>
              </li>
              <li>
                <Link
                  to="/policies"
                  data-testid="footer-link-policies"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t('policies')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-4">
              {t('subscribe_newsletter')}
            </h4>
            <form onSubmit={handleSubscribe} data-testid="newsletter-form" className="space-y-4">
              <input
                type="email"
                data-testid="newsletter-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('email_placeholder')}
                required
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm text-foreground placeholder:text-white/30 focus:outline-none focus:border-primary transition-colors"
              />
              <button
                type="submit"
                data-testid="newsletter-subscribe-button"
                disabled={loading}
                className="w-full border border-primary/50 text-primary hover:bg-primary/10 px-6 py-3 font-serif text-sm uppercase tracking-wider transition-all duration-300 disabled:opacity-50 rounded-full"
              >
                {loading ? '...' : t('subscribe')}
              </button>
            </form>
          </div>
        </div>

        {/* Legal/Policy Links */}
        {settings?.footer_links && settings.footer_links.length > 0 && (
          <div className="mt-8 pt-8 border-t border-white/5">
            <div className="flex flex-wrap justify-center items-center gap-6">
              {settings.footer_links.map((link, index) => {
                const linkName = link.name?.[language] || link.name?.en || '';
                const isExternal = link.url?.startsWith('http');
                
                if (isExternal) {
                  return (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      {linkName}
                    </a>
                  );
                }
                
                return (
                  <Link
                    key={index}
                    to={link.url || '#'}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                  >
                    {linkName}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Collaborators Section */}
        {collaborators.length > 0 && (
          <div className="mt-8 pt-8 border-t border-white/5">
            <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-6 text-center">
              {language === 'ro' ? 'Colaboratori' : language === 'fr' ? 'Collaborateurs' : 'Partners'}
            </h4>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {collaborators.map((collab) => (
                <a
                  key={collab.id}
                  href={collab.website_url || '#'}
                  target={collab.website_url ? '_blank' : '_self'}
                  rel="noopener noreferrer"
                  className="group flex flex-col items-center transition-transform hover:scale-105"
                  title={collab.name}
                >
                  {collab.logo_url ? (
                    <div className="h-12 w-auto flex items-center justify-center bg-white/5 px-4 py-2 rounded border border-white/10 group-hover:border-primary/30 transition-colors">
                      <img
                        src={collab.logo_url}
                        alt={collab.name}
                        className="max-h-8 w-auto object-contain filter grayscale group-hover:grayscale-0 transition-all"
                      />
                    </div>
                  ) : (
                    <div className="h-12 px-4 py-2 bg-white/5 rounded border border-white/10 flex items-center justify-center">
                      <span className="text-xs text-muted-foreground">{collab.name}</span>
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <p className="text-xs text-muted-foreground">{t('footer_text')}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};